import FileUpload from '../FileUpload/FileUpload';
import FileUploadList from '../FileUpload/FileUploadList';
import fileUploadService from '../FileUpload/fileUploadService';
import OrderItem from '../OrderItem/OrderItem';
import PaginatedList from '../Pagination/PaginatedList';
import PaginationOptions from '../Pagination/PaginationOptions';
import RemotePaginatedList from '../Pagination/RemotePaginatedList';
import PrintFile from '../PrintFile/PrintFile';
import { typeCheck } from '../lib/util';
import digitalProductAssetsService from './digitalProductAssetsService';

/**
 * Asset management API interface
 * @namespace Collaterate.Assets
 */

export default class AssetAPI {
    /**
     * Upload print files for an order item
     * @param {OrderItem} item - The item to upload print files to.
     * @param {FileList} fileList - The {@link https://developer.mozilla.org/en-US/docs/Web/API/FileList FileList} of files to upload to this item.
     * @returns {FileUploadList}
     * @memberof Collaterate.Assets
     * @example
     * // Collaterate.Assets.uploadOrderItemPrintFiles returns a {@link FileUploadList FileUploadList} object, which has {@link FileUploadList#progress progress}, {@link FileUploadList#fileUploadComplete fileUploadComplete}, {@link FileUploadList#complete complete} and {@link FileUploadList#fail fail} callbacks
     * // to track the overall progress of all files being uploaded, and of the individual {@link FileUpload FileUploads} where applicable.
     *
     * Collaterate.Assets.uploadOrderItemPrintFiles(orderItem, files)
     *     .progress(onUploadListProgress)
     *     .fileUploadComplete(onFileUploadComplete)
     *     .complete(onUploadListComplete)
     *     .fail(onFileUploadFail);
     *
     * function onUploadListProgress (fileUploadList, fileUpload) {
     *     console.log(`${fileUpload.name} made progress! ${fileUpload.loaded} bytes of ${fileUpload.total} bytes, ${fileUpload.percent}% loaded.`);
     *     // --> "somefile.jpg made progress! 125 bytes of 250 bytes, 50% loaded."
     *
     *     console.log(`The list made progress! ${fileUploadList.loaded} bytes of ${fileUploadList.total} bytes, ${fileUploadList.percent}% loaded.`);
     *     // --> "The list made progress! 250 bytes of 500 bytes, 50% loaded."
     * }
     *
     * function onFileUploadComplete (fileUploadList, fileUpload, printFile) {
     *     console.log(`${fileUpload.name} done uploading? ${fileUpload.complete}!`);
     *     // --> "someFile.jpg done uploading? true!""
     *
     *     console.log(`A new printfile with id: ${printFile.id} was added to your order item.`);
     *     // --> "A new printfile with id: 12345 was added to your order item."
     * }
     *
     * function onUploadListComplete (fileUploadList) {
     *     console.log('Uploading complete. All files either successfuly uploaded or encountered an error.');
     * }
     *
     * function onFileUploadFail (fileUploadList, fileUpload) {
     *     console.log(`${fileUpload.name} encountered an error: ${fileUpload.error.message}`);
     * }
     *
     * @example
     * // In many cases, you might be interested in any change of upload state. Whether the upload progresses, completes or contains failures,
     * // you want to be notified so you can render the current upload state. For this you can use the {@link FileUploadList FileUploadList} {@link FileUploadList#change change} callback,
     * // which acts as a combination of the three other callbacks.
     *
     * Collaterate.Assets.uploadOrderItemPrintFiles(orderItem, files)
     *     .change(onUploadListChange);
     *
     * function onUploadListChange (fileUploadList, fileUpload) {
     *     console.log(`The uploads, they are a changin'...`);
     * }
     */

    static uploadOrderItemPrintFiles (item, fileList, onProgress) {
        typeCheck(item, OrderItem);
        typeCheck(fileList, FileList);
        onProgress && typeCheck(onProgress, Function);

        return new FileUploadList([...fileList].map(file => FileUpload.createForOrderItem(item, file, onProgress)));
    }

    /**
     * Uploads a file without attaching it to anything yet, returning the reservation name to pass
     * to {@link Collaterate.Assets.attachStagedFile attachStagedFile}.
     *
     * Use this when the file is chosen before the item it belongs to exists — for example envelope
     * print-ready artwork picked on the product page, where the envelope job is only auto-added as a
     * companion once the product reaches the cart. The bytes go straight to storage, so nothing has
     * to be held in the browser between pages; carry the returned name instead.
     *
     * A staged file that is never attached expires on its own and needs no cleanup.
     *
     * @param {File} file - The file to stage.
     * @param {function} [onProgress] - Upload progress callback.
     * @returns {Promise<string>} Resolves with the staged file reservation name.
     * @memberof Collaterate.Assets
     * @example
     * // On the product page, before any order item exists:
     * const reservationName = await Collaterate.Assets.stageFile(file);
     * // ...later, once the item exists:
     * await Collaterate.Assets.attachStagedFile(orderItem, reservationName);
     */

    static stageFile (file, onProgress) {
        typeCheck(file, File);
        onProgress && typeCheck(onProgress, Function);

        return fileUploadService.stageFile(file, onProgress);
    }

    /**
     * Attaches a previously staged file to an order item as one of its print files. The bytes were
     * already uploaded by {@link Collaterate.Assets.stageFile stageFile}, so this is a metadata call.
     *
     * @param {OrderItem} item - The item to attach the staged file to.
     * @param {string} stagedFileReservationName - The name returned by stageFile.
     * @returns {Promise} Resolves once the file is attached to the item.
     * @memberof Collaterate.Assets
     * @example
     * Collaterate.Assets.attachStagedFile(companionItem, reservationName)
     *     .then(function () {
     *         console.log('Envelope artwork attached to the companion job.');
     *     });
     */

    static attachStagedFile (item, stagedFileReservationName) {
        typeCheck(item, OrderItem);
        typeCheck(stagedFileReservationName, String);

        return fileUploadService.attachStagedFile(item, stagedFileReservationName);
    }

    /**
     * Deletes an order item print file
     * @param {PrintFile} file - The order item print file to delete
     * @returns {Promise} Promise that is resolved on successful operation or rejected on failure
     * @memberof Collaterate.Assets
     * @example
     * Collaterate.Assets.deleteOrderItemPrintFile(file)
     *     .then(function () {
     *         console.log('File deleted successfully!');
     *     })
     *     .catch(function (e) {
     *         console.log('Something went wrong, file not deleted.');
     *     });
     */

    static async deleteOrderItemPrintFile (file) {
        typeCheck(file, PrintFile);

        return fileUploadService.deleteOrderItemPrintFile(file.id);
    }

    /**
     * Get a paginated list of digital assets from the user's library.
     * @param {number} userId - User id whose library we wish to fetch
     * @param {PaginatedList~paginationOptions} [paginationOptions] - Object of options to control paginated response. Default sort for this endpoint is 'createdOn'.
     * @returns {Promise<PaginatedList>} Returns a Promise that resolves with a {@link PaginatedList PaginatedList}, whose items are {@link Collaterate.Assets~digitalAsset digitalAsset} objects.
     * @memberof Collaterate.Assets
     * @example
     * // Get a paginated list of the user's digital assets, store the PaginatedList response, and log the items to the console
     * let paginatedUserDigitalAssetLibrary;
     *
     * Collaterate.Assets.getUserDigitalAssetLibrary(1234, { page: 2 })
     *     .then(paginatedList => {
     *         paginatedUserDigitalAssetLibrary = paginatedList;
     *         paginatedUserDigitalAssetLibrary.items.forEach(console.log);
     *     });
     */

    static async getUserDigitalAssetLibrary (userId, paginationOptions = {}) {
        typeCheck(userId, Number);
        typeCheck(paginationOptions, Object);

        const pageOptions = new PaginationOptions({
            sort: {
                fieldName: 'createdOn',
                ascending: true,
            },
            ...paginationOptions,
        });

        const { content, page } = await digitalProductAssetsService.getUserLibrary({
            page: pageOptions,
            userId,
        });

        return new PaginatedList(new RemotePaginatedList(content.map(item => ({
            id: item.digitalProductAssetId,
            fileName: item.fileName,
            filePath: item.filePath,
            lastDownloadDate: new Date(item.lastDownloadDate),
            originalDownloadDate: new Date(item.originalDownloadDate),
            orderItemName: item.orderItemName,
            digitalProductName: item.digitalProductName,
            offeringIdWithPrefix: item.offeringIdWithPrefix,

        })), page, this.getUserDigitalAssetLibrary.bind(this, userId)))
    }

    /**
     * Initiate the download of a digital asset. Optionally skip the automatic download and retreive the url to manually manage the download.
     * @param {number} userId - The user id for this user
     * @param {Collaterate.Assets~digitalAsset} digitalAsset - The digital asset object (or an object containing the digital asset's "id" (Number) and "fileName" (String) fields)
     * @param {boolean} [skipDownload = false] - Whether to skip the automatic download action when called.
     * @returns {string} The signed url from which to download the digital asset
     * @memberof Collaterate.Assets
     * @example
     * // Trigger download of the digital asset, then log its url
     * Collaterate.Assets.downloadDigitalAsset(1234, { id: 567, fileName: "SomeFile.pdf" })
     *     .then(function (url) {
     *         console.log(`The asset was downloaded from ${url}.`);
     *     });
     */
    static async downloadDigitalAsset (userId, digitalAsset, skipDownload = false) {
        typeCheck(userId, Number);
        typeCheck(digitalAsset, Object);
        typeCheck(digitalAsset.id, Number);
        typeCheck(digitalAsset.fileName, String);
        typeCheck(skipDownload, Boolean);

        const url = await digitalProductAssetsService.getS3Url(digitalAsset.id);

        if (!skipDownload) {
            const anchor = document.createElement('a');

            anchor.href = url;
            anchor.download = digitalAsset.fileName;
            anchor.rel = 'noopener noreferrer';
            anchor.target = '_blank';
            document.body.append(anchor);
            anchor.click();
            setTimeout(() => {
                anchor.remove();
            }, 0);
        }

        return url;
    }
}

/**
 * Digital Asset object.
 *
 * @typedef {Object} Collaterate.Assets~digitalAsset
 * @property {number} id - The digital asset id
 * @property {string} fileName - Name of the asset
 * @property {string} filePath - The full path of the asset
 * @property {Date} lastDownloadDate - Date of the last download
 * @property {Date} originalDownloadDate - Date of the first download
 * @property {string} orderItemName - TBD, unknown use
 * @property {string} digitalProductName - The product name
 * @property {string} offeringIdWithPrefix - The product offering id with prefix)
 */
